import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Checkbox,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useNetworkList,
  useCreatePrivateNetwork,
  useCreateVirtualRouter,
  networkCidr,
} from '@/api/opennebula';
import { validatePrivateCidr } from '../instances/instance-create/NetworkDialog';

/**
 * Create a virtual router. The backend rejects a router with no networks
 * (`Router.Create.Failed{Network.Empty}`), so creation MUST bind at least one
 * network up front — either existing networks or a brand-new one created here.
 * Mirrors the legacy "Create VRN" flow: pick networks (or create + use one),
 * then POST `/routers` with `network_ids` + `use_reserved_gateway`.
 */
export function RouterCreateDialog({
  serviceId,
  open,
  onOpenChange,
}: {
  serviceId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { show } = useToast();
  const { data: networks = [] } = useNetworkList(open ? serviceId : undefined);

  // Public networks are managed by the platform and can't back a router.
  const selectable = useMemo(() => networks.filter((n) => !/public/i.test(n.name ?? '')), [networks]);
  const hasExisting = selectable.length > 0;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selected, setSelected] = useState<string[]>([]);
  const [netName, setNetName] = useState('');
  const [subnet, setSubnet] = useState('');
  const [netDesc, setNetDesc] = useState('');
  const [touched, setTouched] = useState(false);

  // With no existing networks the only path is to create one.
  const effectiveMode = hasExisting ? mode : 'new';
  const subnetCheck = useMemo(() => (subnet.trim() ? validatePrivateCidr(subnet) : { valid: false, error: undefined as string | undefined }), [subnet]);

  const createNetwork = useCreatePrivateNetwork(serviceId);
  const createRouter = useCreateVirtualRouter(serviceId);
  const pending = createNetwork.isPending || createRouter.isPending;

  const valid =
    name.trim() &&
    (effectiveMode === 'existing' ? selected.length > 0 : netName.trim() && subnetCheck.valid);

  function reset() {
    setName(''); setDescription(''); setMode('existing'); setSelected([]);
    setNetName(''); setSubnet(''); setNetDesc(''); setTouched(false);
  }
  function close(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }
  function toggle(id: string) {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  async function submit() {
    setTouched(true);
    if (!valid) return;
    try {
      let networkIds = [...selected];
      if (effectiveMode === 'new') {
        const res = (await createNetwork.mutateAsync({
          name: netName.trim(),
          description: netDesc.trim() || undefined,
          cidr: subnet.trim(),
          enable_dhcp: true,
        })) as { data?: { network?: { id?: string; name?: string } } };
        const created = res?.data?.network;
        if (!created?.id) { show({ tone: 'danger', title: 'Failed to create network' }); return; }
        networkIds = [created.id];
      }
      await createRouter.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        network_ids: networkIds,
        use_reserved_gateway: true,
      });
      show({ tone: 'success', title: 'Router created' });
      close(false);
    } catch (e) {
      show({ tone: 'danger', title: (e as Error)?.message || 'Create failed' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>New virtual router</DialogTitle>
          <DialogDescription>
            A router connects networks and routes traffic between them. Attach at least one network to create it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Field label="Router name" required>
            {(p) => <Input {...p} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="vr-main" />}
          </Field>

          <Field label="Network configuration" required>
            {() => (
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                {hasExisting && (
                  <SegmentedControl
                    value={mode}
                    onValueChange={(v) => setMode(v as 'existing' | 'new')}
                    items={[
                      { value: 'existing', label: 'Use existing' },
                      { value: 'new', label: 'Create new' },
                    ]}
                  />
                )}

                {effectiveMode === 'existing' ? (
                  <div style={{ display: 'grid', gap: 'var(--vn-space-1)', maxHeight: 220, overflowY: 'auto', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-2)' }}>
                    {selectable.map((n) => {
                      const c = networkCidr(n);
                      return (
                        <label key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', padding: 'var(--vn-space-1) var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
                          <Checkbox checked={selected.includes(String(n.id))} onCheckedChange={() => toggle(String(n.id))} />
                          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{n.name}</span>
                          {c && <code style={{ color: 'var(--vn-fg-muted)' }}>{c}</code>}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                    {!hasExisting && <Alert tone="info">No networks available — create one to back this router.</Alert>}
                    <Field label="Network name" required>
                      {(p) => <Input {...p} value={netName} onChange={(e) => setNetName(e.target.value)} placeholder="net-main" />}
                    </Field>
                    <Field
                      label="Subnet (CIDR)"
                      required
                      description="Gateway IP (.1) is reserved automatically for the router."
                      error={touched && subnet.trim() ? subnetCheck.error : undefined}
                    >
                      {(p) => <Input {...p} value={subnet} invalid={touched && !!subnet.trim() && !subnetCheck.valid} onChange={(e) => setSubnet(e.target.value)} placeholder="e.g. 10.0.1.0/24" />}
                    </Field>
                    <Field label="Network description">
                      {(p) => <Textarea {...p} value={netDesc} onChange={(e) => setNetDesc(e.target.value)} rows={2} placeholder="Optional" />}
                    </Field>
                  </div>
                )}
              </div>
            )}
          </Field>

          <Field label="Router description">
            {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional" />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => close(false)} disabled={pending}>Cancel</Button>
          <Button disabled={!valid || pending} onClick={submit}>{pending ? 'Creating…' : 'Create router'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
